import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import agent from "../../app/api/agent";
import type { FitmentCatalogSettings } from "../../app/models/FitmentCatalog";
import type { StorefrontTranslations } from "../../app/models/StorefrontTranslations";
import { useStore } from "../../app/stores/store";
import { normalizeShopIdentifier } from "../../app/utils/productGuideFeature";
import SettingsPageLayout from "../settings/SettingsPageLayout";
import AppearancePreview from "./AppearancePreview";
import ResultsAppearanceControls from "./ResultsAppearanceControls";
import { getEffectiveUiSettings, getInheritedUiColors, withUiDefaults } from "./storefrontSettingsModel";
import useStorefrontSettings from "./useStorefrontSettings";
import { VehicleModalControls, VehicleModalPreview } from "./VehicleModalAppearance";
import "./AppearanceSettingsPage.css";
import "./StorefrontPreview.css";

// Storefront text as the preview shows it: saved overrides, else built-in defaults.
const previewText = (translations: StorefrontTranslations) => Object.fromEntries(translations.entries.flatMap(entry => {
    const value = entry.value?.trim();
    if (entry.entityType === "SelectorField" && !value && entry.builtInValue === entry.defaultValue) return [];
    return [[entry.key, value || entry.builtInValue || entry.defaultValue]];
}));

function usePreviewData() {
    const [catalog, setCatalog] = useState<FitmentCatalogSettings>();
    const [catalogError, setCatalogError] = useState<string>();
    const [translations, setTranslations] = useState<StorefrontTranslations>();
    const [textError, setTextError] = useState<string>();
    const loadCatalog = useCallback(() => {
        setCatalogError(undefined);
        agent.Settings.fitmentCatalog().then(setCatalog).catch((err: any) => setCatalogError(err?.message ?? "Fitment fields could not be loaded."));
    }, []);
    const loadText = useCallback(() => {
        setTextError(undefined);
        agent.Settings.storefrontTranslations().then(setTranslations).catch((err: any) => setTextError(err?.message ?? "Storefront text could not be loaded."));
    }, []);
    useEffect(() => { loadCatalog(); loadText(); }, [loadCatalog, loadText]);
    return { catalog, catalogError, loadCatalog, translations, textError, loadText };
}

// Editor and live preview side by side, as Shopify asks for visual customizations.
function AppearanceSettingsPage() {
    const navigate = useNavigate();
    const { userStore } = useStore();
    const storefront = useStorefrontSettings();
    const preview = usePreviewData();
    const [area, setArea] = useState<"results" | "modal">("results");
    const { themeDraft, resultsDraft } = storefront;

    if (storefront.loading || !themeDraft || !resultsDraft) return storefront.error
        ? <SettingsPageLayout error={storefront.error}>{null}</SettingsPageLayout>
        : <SettingsPageLayout loading>{null}</SettingsPageLayout>;

    const rawUi = withUiDefaults(resultsDraft.uiSettings);
    const ui = getEffectiveUiSettings(rawUi);
    const collectionAppearance = resultsDraft.renderingMode === "CollectionFiltering";
    const modalArea = collectionAppearance || area === "modal";
    const text = preview.translations ? previewText(preview.translations) : undefined;
    const shop = userStore?.user?.shopDomain ? normalizeShopIdentifier(userStore.user.shopDomain) : undefined;
    const editText = (group: string) => navigate(`/settings/storefront-text?${new URLSearchParams({ group })}`);

    return (
        <SettingsPageLayout error={storefront.error} form={storefront.form} wide
            primaryAction={shop && <s-button slot="secondary-actions" href={`https://${shop}/admin/themes/current/editor`} target="_blank">
                Theme editor
            </s-button>}>
            {!collectionAppearance && <s-stack direction="inline" gap="small-200">
                <s-press-button pressed={area === "results"} onClick={() => setArea("results")}>Product results</s-press-button>
                <s-press-button pressed={area === "modal"} onClick={() => setArea("modal")}>Vehicle modal</s-press-button>
            </s-stack>}
            <div className="pf-appearance-workspace">
                <div className="pf-appearance-workspace__preview">
                    {modalArea
                        ? <VehicleModalPreview active ui={ui} catalog={preview.catalog} text={text} />
                        : <AppearancePreview vehicleBarInTheme={resultsDraft.renderingMode !== "AppProxyResults"} results={resultsDraft}
                            theme={themeDraft} uiSettings={ui} catalog={preview.catalog ?? null}
                            catalogError={!preview.catalog ? preview.catalogError : undefined} onRetryCatalog={preview.loadCatalog}
                            text={text} locale={preview.translations?.selectedLocale}
                            textError={!preview.translations ? preview.textError : undefined} onRetryText={preview.loadText}
                            shopDomain={userStore?.user?.shopDomain} />}
                </div>
                <div className="pf-appearance-workspace__controls">
                    <s-section heading={modalArea ? "Vehicle modal" : "Product results"}>
                        {modalArea
                            ? <VehicleModalControls ui={ui} update={storefront.updateUiSettingsDraft} onEditText={() => editText("Vehicle Search")} />
                            : <ResultsAppearanceControls uiSettings={ui} results={resultsDraft}
                                updateResultsDraft={storefront.updateResultsDraft} updateUiSettingsDraft={storefront.updateUiSettingsDraft}
                                numberDrafts={storefront.appearanceNumberDrafts} onNumberDraftChange={storefront.updateAppearanceNumberDraft}
                                colorDefaults={getInheritedUiColors(rawUi)} onEditText={() => editText("Product Results")} />}
                    </s-section>
                </div>
            </div>
        </SettingsPageLayout>
    );
}

export default observer(AppearanceSettingsPage);
